import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useAuth } from "@/context/providers/AuthProvider";
import { useTicketMutations } from "@/hooks/useTicketMutations";
import { CreatePageState, useCreatePageStore } from "@/stores/createPageStore";
import {
  useFormActions,
  useModalActions,
  useCreditActions,
  useTicketActions,
  useModificationActions,
  useGenerationActions,
  useVariationActions,
  useNavigationActions,
  useAgentActions,
} from "./actions";
import type { OnImageComplete } from "./actions/useGenerationActions";
import { useCreateIdentityBoundary } from "./hooks/useCreateIdentityBoundary";
import { createNotifyAgentOfImageComplete } from "./utils/notifyAgentOfImageComplete";

interface UseCreatePageActionsProps {
  dispatch: (action: any) => void; // Keep for compatibility with effects
  state: CreatePageState;
  isChatMode?: boolean;
}

export function useCreatePageActions({
  dispatch,
  state,
  isChatMode = false,
}: UseCreatePageActionsProps) {
  const store = useCreatePageStore();
  const {
    session,
    roles,
    loading: rolesLoading,
    isAnonymous,
    refreshSession,
  } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Get ticket mutations from React Query
  const {
    createMutation,
    updateMutation,
    submitMutation,
    createModifiedMutation,
  } = useTicketMutations();

  // Ref to track if generation is currently in progress to prevent multiple simultaneous calls
  const isGeneratingRef = useRef<boolean>(false);
  const identityEpochRef = useRef(0);

  // Ref to store latest state to avoid including state in action dependencies
  // This prevents actions from being recreated on every state change
  const stateRef = useRef<CreatePageState>(state);
  useLayoutEffect(() => {
    stateRef.current = state;
  }, [state]);

  // A /create mount can outlive the auth identity that first owned its global
  // Zustand state (for example an in-app sign-out followed by a new anonymous
  // session). Enforce the ownership boundary before any action hook can reuse
  // that state. Same-id refreshes and in-place anonymous upgrades are kept.
  useCreateIdentityBoundary({
    userId: session?.user?.id ?? null,
    authLoading: rolesLoading,
    stateRef,
    isGeneratingRef,
    identityEpochRef,
  });

  useEffect(() => {
    return () => {
      // Invalidate continuations whose transport cannot be aborted. Without a
      // cleanup, the old mount's ref remains unchanged and a late render can
      // write into the next account's global Zustand store.
      identityEpochRef.current += 1;
      const latestStore = useCreatePageStore.getState();
      const hadActiveAgentRequest = Boolean(
        latestStore.agent.activeRequestController
      );
      latestStore.abortActiveRequest();
      // Zustand outlives the /create component tree. Route changes and Fast
      // Refresh therefore used to preserve the aborted request's global
      // "thinking" flags; returning to /create showed a selected card above an
      // endless spinner and kept every action surface inactive. The retired
      // request cannot clear these in its own finally block (its identity epoch
      // is intentionally stale), so teardown must release its transient UI.
      // Do not mutate entry/restoration state during a no-op Strict Mode effect
      // replay: only the teardown that actually retired a request owns these
      // activity flags.
      if (hadActiveAgentRequest) {
        latestStore.setTyping(false);
        latestStore.setThinkingStatus(null);
        latestStore.setThinkingTrace([]);
        latestStore.setPresetsLoading(false);
        latestStore.setEntryTransition("settled");
      }
      isGeneratingRef.current = false;
    };
  }, []);

  // Ref for image-complete notification - set after agentActions exists (avoids circular deps)
  const onImageCompleteRef = useRef<OnImageComplete | null>(null);
  const onImageComplete = useCallback<OnImageComplete>(
    (side, imageUrl, variationUrls, isModification, imageReviewToken) => {
      onImageCompleteRef.current?.(
        side,
        imageUrl,
        variationUrls,
        isModification,
        imageReviewToken
      );
    },
    []
  );

  // Create shared context for all action hooks
  const context = {
    store,
    stateRef,
    session,
    isAnonymous: Boolean(isAnonymous),
    refreshSession,
    roles,
    rolesLoading,
    router,
    pathname,
    searchParams,
    createMutation,
    updateMutation,
    submitMutation,
    createModifiedMutation,
    isGeneratingRef,
    identityEpochRef,
    dispatch,
    isChatMode,
  };

  // Form actions (no dependencies)
  const formActions = useFormActions(context);

  // Ticket actions (needs updateFormData)
  const ticketActions = useTicketActions(context, formActions.updateFormData);

  // Generation actions (needs updateGeneratedTicket, onImageComplete for chat mode)
  const generationActions = useGenerationActions(
    context,
    ticketActions.updateGeneratedTicket,
    isChatMode ? onImageComplete : null
  );

  // Navigation actions (needs generateImages)
  const navigationActions = useNavigationActions(
    context,
    generationActions.generateImages
  );

  // Credit actions (needs submitTicket)
  const creditActions = useCreditActions(context, ticketActions.submitTicket);

  // Modal actions
  const modalActions = useModalActions(context);

  // Modification actions (tickets are created server-side, onModifyComplete for chat mode)
  const modificationActions = useModificationActions(
    context,
    isChatMode ? onImageComplete : null
  );

  // Variation actions (just switches between existing ticket IDs)
  const variationActions = useVariationActions(context, state);

  // Agent actions (needs updateFormData, generateImages, generateBackOnly, submitTicket, syncFormDataToTicket)
  const agentActions = useAgentActions(
    context,
    formActions.updateFormData,
    generationActions.generateImages,
    navigationActions.generateBackOnly,
    ticketActions.submitTicket,
    ticketActions.syncFormDataToTicket,
    modificationActions.modifyImage,
    modificationActions.setModifyPrompt
  );

  // Wire image-complete notification to sendAgentMessage (chat mode only)
  // Done after agentActions so we have sendAgentMessage; uses ref to avoid circular deps
  useEffect(() => {
    if (isChatMode) {
      onImageCompleteRef.current = createNotifyAgentOfImageComplete(
        agentActions.sendAgentMessage
      );
    } else {
      onImageCompleteRef.current = null;
    }
    return () => {
      onImageCompleteRef.current = null;
    };
  }, [isChatMode, agentActions.sendAgentMessage]);

  // Error actions
  const clearWarnings = useCallback(() => {
    store.clearWarnings();
  }, [store]);

  const setError = useCallback(
    (error: string | null) => {
      store.setError(error);
    },
    [store]
  );

  const clearError = useCallback(() => {
    store.clearError();
  }, [store]);

  // UI actions
  const setCurrentView = useCallback(
    (view: "front" | "back") => {
      store.setCurrentView(view);
    },
    [store]
  );

  return {
    // Form actions
    updateFormData: formActions.updateFormData,
    resetForm: formActions.resetForm,
    setValidationError: formActions.setValidationError,

    // Upload actions
    setUploadedFrontImage: formActions.setUploadedFrontImage,
    setUploadedBackImage: formActions.setUploadedBackImage,
    clearUploadedImages: formActions.clearUploadedImages,

    // Navigation actions
    setStep: navigationActions.setStep,
    handleNext: navigationActions.handleNext,
    handlePrevious: navigationActions.handlePrevious,
    handleStartOver: navigationActions.handleStartOver,
    approveFrontDesign: navigationActions.approveFrontDesign,
    generateBackOnly: navigationActions.generateBackOnly,

    // Generation actions
    generateImages: generationActions.generateImages,
    modifyImage: modificationActions.modifyImage,

    // Ticket actions
    submitTicket: ticketActions.submitTicket,
    getPendingQuoteDesignSpec: ticketActions.getPendingQuoteDesignSpec,
    getPendingQuoteScope: ticketActions.getPendingQuoteScope,
    upgradeAndSubmitQuote: ticketActions.upgradeAndSubmitQuote,
    hideQuoteAccountModal: ticketActions.hideQuoteAccountModal,

    // Credit actions
    showCreditModal: creditActions.showCreditModal,
    hideCreditModal: creditActions.hideCreditModal,
    dismissCreditModal: creditActions.dismissCreditModal,
    purchaseCredits: creditActions.purchaseCredits,
    upgradeAndPurchaseCredits: creditActions.upgradeAndPurchaseCredits,
    handleCreditSuccess: creditActions.handleCreditSuccess,
    hideCreditSuccess: creditActions.hideCreditSuccess,
    hideCreditCheckout: creditActions.hideCreditCheckout,

    // Feedback modal actions
    setShowFeedbackModal: modalActions.setShowFeedbackModal,
    hideFeedbackModal: modalActions.hideFeedbackModal,

    // Error actions
    setError,
    clearError,
    clearWarnings,

    // UI actions
    setShowModifyInput: modificationActions.setShowModifyInput,
    setModifyPrompt: modificationActions.setModifyPrompt,
    setShowFullSizeImage: modalActions.setShowFullSizeImage,
    setCurrentView,

    // Variation selection actions
    selectFrontVariation: variationActions.selectFrontVariation,
    selectBackVariation: variationActions.selectBackVariation,

    // Modal setter functions for tests
    setShowCreditModal: modalActions.setShowCreditModal,

    // Internal ticket operations
    updateGeneratedTicket: ticketActions.updateGeneratedTicket,

    // Agent actions
    sendAgentMessage: agentActions.sendAgentMessage,
    processAgentResponse: agentActions.processAgentResponse,
  };
}
